import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { operationType } from '../../../../../services/types';
import { Loader, Main, Styled } from './style';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard, faExternalLinkAlt, faCopy } from '@fortawesome/free-solid-svg-icons';
import { webURL } from '../../../../../services/API';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

type GoogleFormTriggerProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

// Generate the Apps Script code with the webhook URL
const generateAppsScript = (webhookUrl: string) => `function onFormSubmit(e) {
  var webhookUrl = "${webhookUrl}";
  
  try {
    var form = e.source;
    var response = e.response;
    
    var payload = {
      formId: form.getId(),
      formTitle: form.getTitle(),
      timestamp: response.getTimestamp().toISOString(),
      respondentEmail: response.getRespondentEmail() || null,
      responses: response.getItemResponses().map(function(itemResponse) {
        return {
          question: itemResponse.getItem().getTitle(),
          answer: itemResponse.getResponse()
        };
      })
    };
    
    UrlFetchApp.fetch(webhookUrl, {
      method: "post",
      contentType: "application/json",
      payload: JSON.stringify(payload)
    });
    
  } catch (error) {
    console.error("Webhook error:", error);
  }
}`;

export function GoogleFormTrigger({ id, data, setChildMinimize, setChildSettings }: GoogleFormTriggerProps) {
    const { setNodes } = useReactFlow();
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isCopy, setIsCopy] = React.useState<boolean>(false);
    const [isScriptCopy, setIsScriptCopy] = React.useState<boolean>(false);
    const [link, setLink] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>('');
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('id');

    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // Auto-generate webhook URL when expanded (like Slack trigger)
    React.useEffect(() => {
        if (link === '' && isExpanded) {
            setIsLoading(true);
            // Delay to allow project to be saved first
            setTimeout(() => {
                retrieveLink();
            }, 6000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded, data?.triggerCredentials?.webhookUrl]);

    // Retrieve existing link or generate new one
    const retrieveLink = async () => {
        if (data?.triggerCredentials?.webhookUrl) {
            setLink(data.triggerCredentials.webhookUrl);
            setIsLoading(false);
            setError('');
        } else {
            await generateLink();
        }
    };

    const generateLink = async () => {
        try {
            const response = await axios.post(`${webURL}/triggers/googleform/generate-link`, {
                nodeIndex: data.index,
                projectId
            });

            const { nodeId, nodeSecret, webhookUrl } = response.data;

            // Save to frontend state
            setNodes(prev =>
                prev.map(node =>
                    node.data.index === data.index
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                triggerCredentials: { nodeId, nodeSecret, webhookUrl }
                            }
                        }
                        : node
                )
            );

            setLink(webhookUrl);
            setIsLoading(false);

        } catch (err: any) {
            console.error('Error generating Google Form webhook URL:', err);
            const message = err.response?.data?.message || err.message || 'Failed to generate webhook URL';
            if (message.includes('Trigger node does not exist')) {
                setError('Please SAVE the project first (Ctrl+S), then reopen this panel.');
            } else {
                setError(message);
            }
            setIsLoading(false);
        }
    };

    const appsScript = link ? generateAppsScript(link) : '';

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={false}
                style={{ minWidth: '500px' }}
            >
                {isLoading ? <Loader /> : <Styled>
                    {/* Error Section */}
                    <div className="webhook-section">
                        {error && <div className="error-message" style={{ color: '#ff6b6b', marginBottom: '10px', fontSize: '13px' }}>{error}</div>}
                    </div>

                    {/* Instructions Section */}
                    <div className="instructions-section">
                        <h5>How to Connect Your Google Form:</h5>
                        <ol className="steps-list">
                            <li>Open your Google Form in edit mode</li>
                            <li>Click the <strong>three-dot menu</strong> (⋮) → <strong>Script editor</strong></li>
                            <li>Delete any existing code and paste the script below</li>
                            <li>Click <strong>Save</strong> (Ctrl+S)</li>
                            <li>Click <strong>Triggers</strong> (clock icon on left sidebar)</li>
                            <li>Click <strong>+ Add Trigger</strong> and set:
                                <ul style={{ marginTop: '4px', marginBottom: '4px' }}>
                                    <li>Function: <strong>onFormSubmit</strong></li>
                                    <li>Event type: <strong>On form submit</strong></li>
                                </ul>
                            </li>
                            <li>Click <strong>Save</strong> and authorize the script</li>
                            <li>Submit a test response to trigger the workflow</li>
                        </ol>
                    </div>

                    {/* Apps Script Code Section */}
                    {link && (
                        <div className="script-section">
                            <h5>Google Apps Script Code:</h5>
                            <div className="script-container">
                                <CopyToClipboard
                                    text={appsScript}
                                    onCopy={() => {
                                        setIsScriptCopy(true);
                                        setTimeout(() => setIsScriptCopy(false), 1500);
                                    }}
                                >
                                    <button className="copy-script-btn">
                                        <FontAwesomeIcon icon={isScriptCopy ? faCheck : faCopy} />
                                        {isScriptCopy ? 'Copied!' : 'Copy'}
                                    </button>
                                </CopyToClipboard>
                                <pre className="script-code">{appsScript}</pre>
                            </div>
                        </div>
                    )}

                    <a
                        href="https://developers.google.com/apps-script/guides/triggers"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="external-link"
                    >
                        Google Apps Script Documentation
                        <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                    </a>
                </Styled>}
            </ExpandedBlock> : null}
        </Main>
    </>);
}